import { c as _c } from "react/compiler-runtime";
import React from 'react';
import Button from '@precis-digital/kurama/Button';
import Grid from '@precis-digital/kurama/Grid';
import Typography from '@precis-digital/kurama/Typography';
import EditIcon from '@precis-digital/kurama/icons/EditIcon';
import UploadIcon from '@precis-digital/kurama/icons/UploadIcon';
import { styled, alpha } from '@precis-digital/kurama/utils';
import { AvatarWithLabel } from 'shared/components/AvatarWithLabel';
import { type ContentProps, type AvatarDisplayProps, type EditModeWithoutAvatarProps, type EditModeWithAvatarProps, type ViewModeProps, type UploadButtonProps } from './AvatarUploadTypes';
const UploadButton: React.FC<UploadButtonProps> = t0 => {
  const $ = _c(14);
  const {
    uploadErrorMessage,
    isUploading,
    handleButtonClick,
    t
  } = t0;
  if (uploadErrorMessage !== null && uploadErrorMessage !== "") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <UploadIcon />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== t) {
      t2 = t("avatarUpload.upload");
      $[1] = t;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== handleButtonClick || $[4] !== isUploading || $[5] !== t2) {
      t3 = <StyledErrorButton disabled={isUploading} onClick={handleButtonClick} scheme="light" variant="filled" leftIcon={t1}>{t2}</StyledErrorButton>;
      $[3] = handleButtonClick;
      $[4] = isUploading;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t1;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <UploadIcon />;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t) {
    t2 = t("avatarUpload.upload");
    $[8] = t;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== handleButtonClick || $[11] !== isUploading || $[12] !== t2) {
    t3 = <Button disabled={isUploading} onClick={handleButtonClick} scheme="light" variant="filled" leftIcon={t1}>{t2}</Button>;
    $[10] = handleButtonClick;
    $[11] = isUploading;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
const EditModeWithoutAvatar: React.FC<EditModeWithoutAvatarProps> = t0 => {
  const $ = _c(25);
  const {
    title,
    uploadErrorMessage,
    t,
    isUploading,
    setIsEdit,
    setUploadErrorMessage,
    handleButtonClick
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <Typography variant="body2">{title}</Typography>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t || $[3] !== uploadErrorMessage) {
    t2 = uploadErrorMessage !== null && uploadErrorMessage !== "" ? <Typography color="error" variant="body3">{uploadErrorMessage}</Typography> : <Typography variant="body3">{t("avatarUpload.description")}</Typography>;
    $[2] = t;
    $[3] = uploadErrorMessage;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <Grid>{t1}{t2}</Grid>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== handleButtonClick || $[9] !== isUploading || $[10] !== t || $[11] !== uploadErrorMessage) {
    t4 = <UploadButton uploadErrorMessage={uploadErrorMessage} isUploading={isUploading} handleButtonClick={handleButtonClick} t={t} />;
    $[8] = handleButtonClick;
    $[9] = isUploading;
    $[10] = t;
    $[11] = uploadErrorMessage;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== setIsEdit || $[14] !== setUploadErrorMessage) {
    t5 = () => {
      setIsEdit(false);
      setUploadErrorMessage(null);
    };
    $[13] = setIsEdit;
    $[14] = setUploadErrorMessage;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== isUploading || $[17] !== t5) {
    t6 = <Button onClick={t5} scheme="light" variant="tonal" disabled={isUploading}>Cancel</Button>;
    $[16] = isUploading;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t4 || $[20] !== t6) {
    t7 = <Grid>{t4}{t6}</Grid>;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t3 || $[23] !== t7) {
    t8 = <Grid>{t3}{t7}</Grid>;
    $[22] = t3;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
};
const AvatarDisplay: React.FC<AvatarDisplayProps> = t0 => {
  const $ = _c(23);
  const {
    isAvatarSet,
    isUploadSuccess,
    handleOnImageError,
    client,
    user,
    t,
    avatarVersion
  } = t0;
  if (isAvatarSet || isUploadSuccess) {
    let t1;
    if ($[0] !== avatarVersion || $[1] !== client || $[2] !== handleOnImageError || $[3] !== user) {
      t1 = <AvatarWithLabel handleOnImageError={handleOnImageError} size="medium" client={client} user={user} avatarVersion={avatarVersion} />;
      $[0] = avatarVersion;
      $[1] = client;
      $[2] = handleOnImageError;
      $[3] = user;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== t) {
      t2 = t("avatarUpload.photoUpload");
      $[5] = t;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== t2) {
      t3 = <Grid marginTop={1}><Typography variant="body3">{t2}</Typography></Grid>;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== t) {
      t4 = t("avatarUpload.lookingGood");
      $[9] = t;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== t4) {
      t5 = <Grid marginTop={-1}><StyledTypography variant="body3">{t4}</StyledTypography></Grid>;
      $[11] = t4;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== t3 || $[14] !== t5) {
      t6 = <Grid marginLeft={2} marginTop={-1}>{t3}{t5}</Grid>;
      $[13] = t3;
      $[14] = t5;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    let t7;
    if ($[16] !== t1 || $[17] !== t6) {
      t7 = <Grid paddingTop={1} container={true}>{t1}{t6}</Grid>;
      $[16] = t1;
      $[17] = t6;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    return t7;
  }
  let t1;
  if ($[19] !== t) {
    t1 = t("avatarUpload.notProvided");
    $[19] = t;
    $[20] = t1;
  } else {
    t1 = $[20];
  }
  let t2;
  if ($[21] !== t1) {
    t2 = <Typography variant="body3">{t1}</Typography>;
    $[21] = t1;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  return t2;
};
const EditModeWithAvatar: React.FC<EditModeWithAvatarProps> = t0 => {
  const $ = _c(14);
  const {
    title,
    isUploading,
    handleButtonClick,
    avatarDisplayProps
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <Typography variant="body2">{title}</Typography>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== avatarDisplayProps) {
    t2 = <AvatarDisplay {...avatarDisplayProps} />;
    $[2] = avatarDisplayProps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Grid>{t1}{t2}</Grid>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <EditIcon />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== handleButtonClick || $[9] !== isUploading) {
    t5 = <Grid marginTop={3}><Button disabled={isUploading} variant="text" leftIcon={t4} onClick={handleButtonClick} /></Grid>;
    $[8] = handleButtonClick;
    $[9] = isUploading;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <>{t3}{t5}</>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
const ViewMode: React.FC<ViewModeProps> = t0 => {
  const $ = _c(16);
  const {
    title,
    isAvatarSet,
    isUploading,
    handleButtonClick,
    setIsEdit,
    tWorkspace,
    avatarDisplayProps
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <Typography variant="body2">{title}</Typography>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== avatarDisplayProps) {
    t2 = <AvatarDisplay {...avatarDisplayProps} />;
    $[2] = avatarDisplayProps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Grid>{t1}{t2}</Grid>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== handleButtonClick || $[8] !== isAvatarSet || $[9] !== isUploading || $[10] !== setIsEdit || $[11] !== tWorkspace) {
    t4 = <Grid paddingTop={4}>{isAvatarSet ? <Button disabled={isUploading} variant="text" leftIcon={<EditIcon />} onClick={handleButtonClick} /> : <Button variant="text" onClick={() => {
        setIsEdit(true);
      }}>{tWorkspace("buttons.change")}</Button>}</Grid>;
    $[7] = handleButtonClick;
    $[8] = isAvatarSet;
    $[9] = isUploading;
    $[10] = setIsEdit;
    $[11] = tWorkspace;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
export const Content: React.FC<ContentProps> = t0 => {
  const $ = _c(7);
  const {
    isEdit,
    isUploadSuccess,
    isAvatarSet,
    editModeWithAvatarProps,
    editModeWithoutAvatarProps,
    viewModeProps
  } = t0;
  if (isEdit || isUploadSuccess) {
    let t1;
    if ($[0] !== editModeWithAvatarProps || $[1] !== editModeWithoutAvatarProps || $[2] !== isAvatarSet || $[3] !== isUploadSuccess) {
      t1 = isAvatarSet || isUploadSuccess ? <EditModeWithAvatar {...editModeWithAvatarProps} /> : <EditModeWithoutAvatar {...editModeWithoutAvatarProps} />;
      $[0] = editModeWithAvatarProps;
      $[1] = editModeWithoutAvatarProps;
      $[2] = isAvatarSet;
      $[3] = isUploadSuccess;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] !== viewModeProps) {
    t1 = <ViewMode {...viewModeProps} />;
    $[5] = viewModeProps;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
const StyledErrorButton = styled(Button)(({
  theme
}) => ({
  backgroundColor: theme.tokens.background.semantic.error.main.default,
  borderColor: theme.tokens.background.semantic.error.main.pressed,
  '&:hover': {
    borderColor: theme.tokens.background.semantic.error.main.pressed,
    backgroundColor: alpha(theme.tokens.background.semantic.error.main.default, 0.8)
  }
}));
const StyledTypography = styled(Typography)(({
  theme
}) => ({
  color: theme.tokens.foreground.main.secondary
}));